<template>
  <div class="bg-white rounded-xl border border-gray-200/60">
    <div class="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
      <div class="min-w-0">
        <h2 class="text-[14px] font-semibold text-gray-900 truncate">{{ title }}</h2>
        <p v-if="subtitle" class="text-[12px] text-gray-400 mt-0.5 truncate">{{ subtitle }}</p>
      </div>
      <span v-if="!loading && hasData" class="text-[12px] font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-2.5 py-0.5 rounded">
        {{ formatNumber(total) }}
      </span>
    </div>
    <div class="p-4">
      <LoadingBlock v-if="loading" height="220" label="Loading..." />
      <template v-else-if="hasData">
        <div class="h-[220px] relative animate-fade-in"><canvas ref="canvas"></canvas></div>
      </template>
      <div v-else class="py-10 text-center text-[13px] text-gray-400">{{ emptyLabel }}</div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import LoadingBlock from '../../../components/LoadingBlock.vue'

const PALETTE = ['#3393ff', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280', '#ec4899', '#14b8a6']

/**
 * Donut-chart card used by the overview for distribution breakdowns
 * (devices, OS, browsers, gender). Wraps Chart.js so each call site
 * doesn't have to manage the canvas ref + lifecycle. Designed as a
 * sibling to `BreakdownCard` — donut for distribution-shaped data,
 * progress bars for ranked-list-shaped data.
 *
 * The watcher rebuilds the chart when `data` changes; legend lives
 * below the donut so cards stay narrow enough to fit three across.
 */
export default {
  name: 'DonutCard',
  components: { LoadingBlock },
  props: {
    title:      { type: String, required: true },
    subtitle:   { type: String, default: '' },
    loading:    { type: Boolean, default: false },
    chartKey:   { type: String, required: true },
    data:       { type: Array, default: () => [] },
    emptyLabel: { type: String, default: 'No data' }
  },
  computed: {
    hasData() {
      return Array.isArray(this.data) && this.data.length > 0
    },
    total() {
      return this.data.reduce((sum, d) => sum + (d.count || 0), 0)
    }
  },
  watch: {
    // Destroy first, then re-render — keeps a zombie chart from
    // animating into a v-if-removed canvas after a data swap.
    data: { handler() { this.destroy(); this.$nextTick(() => this.render()) }, deep: true },
    // Loading toggles the v-if branch too, so the canvas may be
    // remounted with a fresh DOM node. Tear down on every flip.
    loading() { this.destroy(); this.$nextTick(() => this.render()) }
  },
  mounted() {
    this.$nextTick(() => this.render())
  },
  beforeUnmount() {
    this.destroy()
  },
  methods: {
    destroy() {
      if (this.chart) { this.chart.destroy(); this.chart = null }
    },
    render() {
      // Bail without leaking a stale chart. `destroy()` already ran
      // from the watcher; this is a belt-and-suspenders for the
      // initial mount path where data starts empty.
      if (this.loading || !this.hasData) { this.destroy(); return }
      const ctx = this.$refs.canvas
      if (!ctx) return
      this.destroy()
      const labels = this.data.map(d => this.fmt(d._id))
      const values = this.data.map(d => d.count)
      this.chart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{ data: values, backgroundColor: PALETTE.slice(0, this.data.length) }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '65%',
          // Disable open-ended animations — they're the path through
          // which a torn-down canvas surfaces the `clipArea` null-ref
          // crash, and on cards that re-render on every store
          // commit the animation barely registers anyway.
          animation: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: { font: { family: 'Poppins', size: 11 }, padding: 12, usePointStyle: true, pointStyle: 'circle' }
            }
          }
        }
      })
    },
    fmt(t) {
      if (!t) return 'Unknown'
      // Capitalize device/OS strings; leave gender values as-is.
      return String(t).charAt(0).toUpperCase() + String(t).slice(1)
    },
    formatNumber(n) {
      if (n == null || isNaN(n)) return '—'
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
      if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K'
      return Number(n).toLocaleString()
    }
  }
}
</script>
